import { isPatchMap, PatchMap } from '@simonbackx/simple-encoding';
import { AccessRight } from './AccessRight.js';
import { PermissionLevel } from './PermissionLevel.js';
/**
 * More granular access rights to specific things in the system
 */
export var PermissionsResourceType;
(function (PermissionsResourceType) {
    PermissionsResourceType["Webshops"] = "Webshops";
    PermissionsResourceType["Groups"] = "Groups";
    PermissionsResourceType["GroupCategories"] = "GroupCategories";
    PermissionsResourceType["OrganizationTags"] = "OrganizationTags";
    PermissionsResourceType["RecordCategories"] = "RecordCategory";
    /**
     * Sending emails and other communication via a sender and viewing the history of sent messages
     */
    PermissionsResourceType["Senders"] = "Senders";
})(PermissionsResourceType || (PermissionsResourceType = {}));
export const PermissionsResourceKey = {
    All: '$all',
    CurrentPeriod: '$currentPeriod',
};
export function isPeriodScopedResourceType(type) {
    switch (type) {
        case PermissionsResourceType.Groups:
        case PermissionsResourceType.GroupCategories:
            return true;
        case PermissionsResourceType.Webshops:
        case PermissionsResourceType.OrganizationTags:
        case PermissionsResourceType.RecordCategories:
        case PermissionsResourceType.Senders:
            return false;
        default: {
            const t = type;
            throw new Error('Unknown resource type ' + t);
        }
    }
}
/**
 * Wildcard keys whose grants also apply to the resource with the given id (the id itself excluded).
 */
export function getWildcardResourceKeys(type, id) {
    if (id === PermissionsResourceKey.All) {
        return [];
    }
    if (id === PermissionsResourceKey.CurrentPeriod || !isPeriodScopedResourceType(type)) {
        return [PermissionsResourceKey.All];
    }
    return [PermissionsResourceKey.All, PermissionsResourceKey.CurrentPeriod];
}
/**
 * old key '' (meaning all resources) is replaced with:
 *  - '$currentPeriod' for period scoped resource types and
 *  - '$all' for non-period scoped resource types.
 */
export function upgradeResourceKeys(resources) {
    // Both map levels can independently be replacements or patches.
    const upgraded = isPatchMap(resources) ? new PatchMap() : new Map();
    for (const [type, values] of resources) {
        if (values === null) {
            upgraded.set(type, null);
            continue;
        }
        const upgradedValues = isPatchMap(values) ? new PatchMap() : new Map();
        for (const [id, value] of values) {
            if (id === '') {
                upgradedValues.set(isPeriodScopedResourceType(type) ? PermissionsResourceKey.CurrentPeriod : PermissionsResourceKey.All, value);
                continue;
            }
            upgradedValues.set(id, value);
        }
        upgraded.set(type, upgradedValues);
    }
    return upgraded;
}
/**
 * Clients before version 418 only know one wildcard: '' (meaning all resources).
 */
export function downgradeResourceKeys(resources) {
    const downgraded = isPatchMap(resources) ? new PatchMap() : new Map();
    for (const [type, values] of resources) {
        if (values === null) {
            downgraded.set(type, null);
            continue;
        }
        const downgradedValues = isPatchMap(values) ? new PatchMap() : new Map();
        for (const [id, value] of values) {
            if (id === PermissionsResourceKey.All) {
                downgradedValues.set('', value);
                continue;
            }
            if (id === PermissionsResourceKey.CurrentPeriod) {
                // when both the current period and the all value are set, only keep the all value
                if (!values.has(PermissionsResourceKey.All)) {
                    downgradedValues.set('', value);
                }
                continue;
            }
            downgradedValues.set(id, value);
        }
        downgraded.set(type, downgradedValues);
    }
    return downgraded;
}
export function getPermissionResourceTypeName(type, plural = true) {
    switch (type) {
        case PermissionsResourceType.Webshops: return plural ? 'webshops' : 'webshop';
        case PermissionsResourceType.Groups: return plural ? $t(`%n1`) : $t(`%14Z`);
        case PermissionsResourceType.GroupCategories: return plural ? $t(`%15H`) : $t(`%n2`);
        case PermissionsResourceType.OrganizationTags: return plural ? $t(`%2C`) : $t(`%2T`);
        case PermissionsResourceType.RecordCategories: return plural ? $t(`%n3`) : $t(`%n4`);
        case PermissionsResourceType.Senders: return plural ? $t(`%1DI`) : $t(`%1DJ`);
    }
}
export function getConfigurableAccessRightsForResourceType(type) {
    switch (type) {
        case PermissionsResourceType.Senders:
            return [AccessRight.SendMessages];
    }
    return [];
}
export function getConfigurablePermissionLevelsForResourceType(type) {
    switch (type) {
        case PermissionsResourceType.Senders:
            return [PermissionLevel.None, PermissionLevel.Read, PermissionLevel.Write];
    }
    return [PermissionLevel.None, PermissionLevel.Read, PermissionLevel.Write, PermissionLevel.Full];
}
export function getDefaultAccessRightsForResourceType(type) {
    switch (type) {
        case PermissionsResourceType.Senders:
            return [AccessRight.SendMessages];
    }
    return [];
}
export function getDefaultPermissionLevelForResourceType(type) {
    switch (type) {
        case PermissionsResourceType.Senders:
            return PermissionLevel.None;
    }
    return PermissionLevel.Full;
}
//# sourceMappingURL=PermissionsResourceType.js.map